import React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import { LineupVideoLivePlayer } from "~components/shared/video-player/LineupVideoLivePlayer";
import { LineupVideoVodPlayer } from "~components/shared/video-player/LineupVideoVodPlayer";
import { LineupVideoVodPlayerSkylight } from "~components/shared/video-player/LineupVideoVodPlayerSkylight";
import { LoopingVideo } from "~components/shared/video-player/LoopingVideo";
import { VideoPlayerProvider } from "~components/shared/video-player/VideoPlayerProvider";
import type { CuratedItem } from "~types/types";

import { DEFAULT_LIVESTREAM, DEFAULT_MEDIA_CONFIG } from "../";
import styles from "../Media.module.css";
import { isVideoMediaFormat } from "../utils/internal";
import {
    type HydratedVodVideoData,
    type HydratedVerticalVideoData,
    type HydratedLoopingVideoData,
    type MediaChildProps,
    MediaFormat,
} from "../utils/types";

/**
 * Confirm valid data exists for at least one
 * Video MediaFormat
 */
export function hasValidVideoData(
    source: CuratedItem,
    format: MediaFormat
): boolean {
    if (!source || !isVideoMediaFormat(format)) return false;

    switch (format) {
        case MediaFormat.vodVideo:
            return Boolean(source?.media?.vodVideo?.video);
        case MediaFormat.livestreamVideo:
            return Boolean(source?.media?.livestreamVideo);
        case MediaFormat.loopingVideo:
            return Boolean(source?.media?.loopingVideo?.video);
        case MediaFormat.verticalVideo:
            return Boolean(source?.media?.verticalVideo?.video);
        default:
            return false;
    }
}

export function VideoMedia(props: MediaChildProps) {
    const { source, format, config = DEFAULT_MEDIA_CONFIG } = props;
    const { showCaption = true } = config;

    if (!hasValidVideoData(source, format)) return null;

    const componentID = `media--${format}`;

    if (format === MediaFormat.livestreamVideo) {
        return (
            <VideoPlayerProvider controls={false} autoplay>
                <section
                    className={createClasses([
                        styles.livestreamVideo,
                        config.className && config.className,
                    ])}
                    data-component={componentID}
                >
                    <LineupVideoLivePlayer
                        livestream={
                            source?.media?.livestreamVideo || DEFAULT_LIVESTREAM
                        }
                    />
                </section>
            </VideoPlayerProvider>
        );
    }

    if (format === MediaFormat.vodVideo) {
        const { video, image } =
            (source?.media?.vodVideo as HydratedVodVideoData) || {};

        if (!video) return null;

        const shouldShowVideoCaption =
            showCaption && !video?.hideCaptionByRequest;

        return (
            <VideoPlayerProvider autoplay={false} startMuted={false}>
                <section
                    className={createClasses([
                        styles.vodVideo,
                        config.className && config.className,
                    ])}
                    data-component={componentID}
                >
                    <LineupVideoVodPlayer
                        showCaption={shouldShowVideoCaption}
                        video={video}
                        image={image}
                        credit={video?.hydratedCredit}
                        fillContainer
                        fillPosition="top"
                    />
                </section>
            </VideoPlayerProvider>
        );
    }

    if (format === MediaFormat.loopingVideo) {
        const { video } =
            (source?.media?.loopingVideo as HydratedLoopingVideoData) || [];
        if (!video) return null;

        // loopingVideo uses a thumbnail with dynamic height
        // and does not constrain its dimensions, this means
        // it will render in the given width with height
        // determined by the thumbnail's aspectRatio. If your
        // use case calls for a constrained aspect ratio,
        // you must add styles in the consuming component.
        // see LineupContent4Up.module.css for an example.
        const { url, thumbnail } = video;
        const { url: itemUrl } = source;
        if (!url) return null;

        const { desktopMaxWidth = 0, mobileMaxWidth = 0, width } = config;
        const imageWidth = width || Math.max(desktopMaxWidth, mobileMaxWidth);
        const shouldShowVideoCaption =
            showCaption && !video?.hideCaptionByRequest;

        return (
            <a
                href={itemUrl}
                className={createClasses([
                    styles.imageOverlayLink,
                    styles.linkedImage,
                ])}
                data-component="image-media-link"
            >
                <section
                    className={createClasses([
                        styles.image,
                        styles.loopingVideo,
                        config.className && config.className,
                    ])}
                    data-component={componentID}
                >
                    <LoopingVideo
                        credit={video.hydratedCredit}
                        url={url}
                        thumbnail={thumbnail}
                        showCaption={shouldShowVideoCaption}
                        imageWidth={imageWidth}
                    />
                </section>
            </a>
        );
    }

    if (format === MediaFormat.verticalVideo) {
        const { video, image } =
            (source?.media?.verticalVideo as HydratedVerticalVideoData) || {};

        if (!video) return null;
        const { hydratedCredit, isVideoCurationOverride } = video;

        const shouldShowVideoCaption =
            showCaption && !video?.hideCaptionByRequest;

        return (
            <section
                className={createClasses([
                    styles.verticalVideo,
                    config.className && config.className,
                ])}
                data-component={componentID}
            >
                <LineupVideoVodPlayerSkylight
                    showCaption={shouldShowVideoCaption}
                    video={video}
                    image={image}
                    credit={hydratedCredit}
                    isVerticalVideo
                    hideInitialTitle={isVideoCurationOverride}
                />
            </section>
        );
    }

    return (
        <div className={styles.video} data-component={componentID}>
            <div>{format}</div>
        </div>
    );
}
